/*
 * Store furniture: capsules, rails, heroes.
 *
 * A storefront is a grid of other studios' art. That is the single biggest
 * difference between this and a business site, and it is why the brand puts
 * the store on Paper — on Paper the art reads as art, hung in a gallery; on
 * black it blurs into every other dark launcher.
 *
 * Flat fields and rules throughout. No gradients, anywhere, ever.
 */

/* --- Capsule ------------------------------------------------------------ */

/*
 * The link, and the frame the card turns inside.
 *
 * The perspective is here and the rotation is on `.capsuleFace`, which is the
 * only arrangement that works — an element cannot supply its own perspective,
 * and one that tries flattens to a scale.
 *
 * 700px is a short lens on purpose. A capsule is an object about the size of a
 * hand, and a longer lens on something that small takes the perspective out of
 * it altogether. The card carries no border and no ground of its own any more;
 * both moved to the face, because the thing that turns has to be the thing that
 * has edges.
 */
.capsule {
  /*
   * How much of the script's angle this card takes.
   *
   * The script writes six degrees at the edge. Six degrees on a 17rem capsule
   * is a shimmer nobody notices; twelve is a card being picked up, which is the
   * effect this is for. Stated as a gain rather than as a second constant in
   * the script, so the page keeps one number and each surface says how much of
   * it it wants.
   */
  --tilt-gain: 2;
  /*
   * How far the accent frame behind the card stands proud of it.
   *
   * It has to survive the perspective. At 34px behind a 700px lens everything
   * back there is drawn at 700 / 734, about 95%, so a frame inset by −12px on
   * every side comes back to a few pixels of accent showing all round. Inset it
   * by less and the frame disappears inside the card and never shows at all,
   * however far it turns; by more and it reaches across the gutter into the
   * card next door. Twelve is exactly half of `--gb-space-6`, the gap both the
   * grid and the rail are set with, so it reaches the middle of the gutter and
   * no further.
   */
  --halo-spread: 12px;
  --halo-depth: 34px;

  display: flex;
  min-width: 0;
  perspective: 700px;
  text-decoration: none;
  color: var(--gb-text);
}

/*
 * A card being looked at stands in front of its neighbours.
 *
 * It grows in three ways at once — the lift, the accent frame standing proud,
 * and the perspective scaling everything that comes forward — and all three
 * reach past the grid cell. Without this the card next door, painted later,
 * clips the one being looked at.
 */
.capsule:hover,
.capsule:focus-visible {
  z-index: 1;
}

/*
 * The face: the card itself, and the thing that turns.
 *
 * `preserve-3d` is what makes the layers inside it real rather than painted —
 * the type in front, the accent frame behind — and it is also why nothing in
 * here may take `overflow: hidden`, which silently flattens the whole subtree
 * back into the page. The one element that does clip is `.capsuleArt`, whose
 * only child is an image and which wants to be flat anyway.
 *
 * At rest there is no transform at all, deliberately. An identity transform
 * still promotes the element to a layer of its own for the whole visit, and a
 * layer is where type stops being rendered against the page's own pixel grid —
 * a whole shelf of it, for a page nobody is pointing at yet.
 *
 * Transform rather than a box-shadow, here as everywhere: shadows read as soft,
 * and nothing in this system is soft.
 */
.capsuleFace {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  transform-style: preserve-3d;
  background: var(--gb-surface-raised);
  border: 1px solid var(--gb-rule);
  transition:
    border-color var(--gb-motion) var(--gb-ease),
    transform var(--gb-motion-slow) var(--gb-ease-out);
}

.capsule:hover .capsuleFace,
.capsule:focus-visible .capsuleFace {
  border-color: var(--gb-accent);
  transform: translateY(-3px);
}

/*
 * While the pointer is on it, the card follows at a hundred and twenty
 * milliseconds and in a straight line — enough to smooth a jittery mouse, not
 * enough to lag behind it. Easing here would make the card feel like it was
 * being dragged through treacle. Leaving puts the slow curve back, so it
 * settles rather than snapping.
 *
 * The angles default to nought, so the one frame between the attribute landing
 * and the first angle being written is a lift and nothing else. The lift is
 * restated rather than inherited: this rule replaces the `translateY` above
 * outright, and a card that dropped back onto the page the instant it started
 * turning is the version that looks broken.
 */
.capsule[data-tilting] .capsuleFace {
  transform: translateY(-3px) rotateX(calc(var(--tilt-x, 0deg) * var(--tilt-gain)))
    rotateY(calc(var(--tilt-y, 0deg) * var(--tilt-gain)));
  transition:
    border-color var(--gb-motion) var(--gb-ease),
    transform var(--gb-motion-fast) linear;
  will-change: transform;
}

/*
 * The accent frame behind the card.
 *
 * A flat rectangle of the accent, standing a little proud of the card and a
 * good way behind it, so at rest it is hidden exactly behind the card and a
 * turn brings it out from whichever edge is going away. That is the whole of
 * the depth cue: a brand with no shadows and no glows has an edge and a colour,
 * and this is both.
 *
 * It is a border rather than a fill, so what shows is a line rather than a
 * slab — and it fades in with the card rather than being there all along,
 * because a grid of permanently haloed cards is a page of highlighted things
 * with nothing to distinguish the one being looked at.
 */
.capsuleHalo {
  position: absolute;
  inset: calc(var(--halo-spread) * -1);
  border: 2px solid var(--gb-accent);
  transform: translateZ(calc(var(--halo-depth) * -1));
  opacity: 0;
  transition: opacity var(--gb-motion) var(--gb-ease-out);
  pointer-events: none;
}

.capsule:hover .capsuleHalo,
.capsule:focus-visible .capsuleHalo {
  opacity: 1;
}

/*
 * The two pieces of type that leave the card.
 *
 * The title comes furthest forward and the studio credit a little less, so the
 * two separate from each other as well as from the art — one plane of type
 * moving as a block would read as a sticker rather than as depth.
 *
 * Only the type, never the plate it sits on. A lifted panel is scaled up by the
 * perspective and overflows the frame it is part of; type has padding all round
 * it that can absorb the same few per cent.
 *
 * `translateZ` on hover and focus rather than on the tilt, so a card reached by
 * keyboard gets the same card as one reached by pointer. The turn is the
 * pointer's; the depth is the card's.
 */
.capsuleTitle,
.capsuleStudio {
  transition: transform var(--gb-motion-slow) var(--gb-ease-out);
}

.capsule:hover .capsuleTitle,
.capsule:focus-visible .capsuleTitle {
  transform: translateZ(40px);
}

.capsule:hover .capsuleStudio,
.capsule:focus-visible .capsuleStudio {
  transform: translateZ(24px);
}

/*
 * The art moves before the card does.
 *
 * A slow, small push in on the image inside a fixed frame — the one piece of
 * motion in the store that is about the game rather than about the interface,
 * which is why it is the only one allowed to touch the art. Capped at 1.04:
 * far enough to register, not far enough to soften a pixel-art capsule.
 */
.capsuleArt img {
  transition: transform var(--gb-motion-slow) var(--gb-ease-out);
}

.capsule:hover .capsuleArt img,
.capsule:focus-visible .capsuleArt img {
  transform: scale(1.04);
}

/* The price steps to the accent, so the eye lands where the action is. */
.capsulePrice {
  transition: color var(--gb-motion) var(--gb-ease);
}

.capsule:hover .capsulePrice {
  color: var(--gb-accent);
}

/*
 * Reduced motion.
 *
 * Everything that travels is cancelled rather than shortened. The global reset
 * in `tokens.css` makes transitions instant, which for a turn means the card
 * snapping to each new angle rather than not turning at all.
 *
 * The halo stays, because it is an opacity rather than a transform — a card
 * saying which one it is, in the accent, exactly like the border it shares an
 * edge with. That is information about what is being looked at rather than
 * motion, and it is the same exception every other accent-on-hover in this file
 * takes. The script that writes the angles is not built at all under reduced
 * motion, so `data-tilting` never appears; the rule below is the belt to that
 * braces.
 */
@media (prefers-reduced-motion: reduce) {
  .capsule:hover .capsuleFace,
  .capsule:focus-visible .capsuleFace,
  .capsule[data-tilting] .capsuleFace,
  .capsule:hover .capsuleTitle,
  .capsule:focus-visible .capsuleTitle,
  .capsule:hover .capsuleStudio,
  .capsule:focus-visible .capsuleStudio,
  .capsule:hover .capsuleArt img,
  .capsule:focus-visible .capsuleArt img {
    transform: none;
    will-change: auto;
  }
}

.capsuleArt {
  aspect-ratio: 16 / 9;
  position: relative;
  overflow: hidden;
  /* Art sits on Ink or Paper, never on a channel colour. */
  background: var(--gb-ink);
  display: flex;
  align-items: flex-end;
  padding: var(--gb-space-3);
}

.capsulePortrait .capsuleArt {
  /* 600x900, the library capsule ratio from the asset targets. */
  aspect-ratio: 2 / 3;
}

.capsuleArt img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/*
 * The art-pending plate.
 *
 * Deliberately looks like a plate rather than like a screenshot. A blurred
 * gradient or a stock image would read as art that failed to load, and a
 * developer seeing it would think the page was broken rather than that they
 * had not uploaded a capsule yet.
 */
.capsulePlate {
  font-family: var(--gb-font-display);
  font-variation-settings:
    'wdth' 125,
    'wght' 900;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 0.86;
  font-size: clamp(1.25rem, 2.2vw, 1.75rem);
  color: var(--gb-paper);
  position: relative;
  z-index: 1;
  max-width: 14ch;
}

.capsuleBody {
  padding: var(--gb-space-3) var(--gb-space-4) var(--gb-space-4);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
  flex: 1;
}

.capsuleTitle {
  font-family: var(--gb-font-display);
  font-variation-settings:
    'wdth' 112,
    'wght' 800;
  text-transform: uppercase;
  font-size: 1.0625rem;
  line-height: 1.05;
  letter-spacing: 0.01em;
  margin: 0;
}

.capsuleStudio {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gb-text-faint);
}

.capsuleFoot {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gb-space-3);
  flex-wrap: wrap;
}

.capsuleAmount {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

/*
 * What the amount is, when it is not the price.
 *
 * The creators site puts commission where a shopper expects a price, and an
 * unlabelled number on a capsule reads as what the game costs. Set in the data
 * label treatment so it reads as a caption on the figure rather than as a
 * second figure.
 */
.capsulePriceLabel {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-faint);
}

.capsulePrice {
  font-family: var(--gb-font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-weight: 700;
  font-size: 1.0625rem;
  letter-spacing: -0.02em;
}

/*
 * Where a title is, in the slot where its price would be (#138, #139).
 *
 * A coming-soon title has no public price, so the price slot is free and is
 * also the one place on the capsule a shopper already looks to find out whether
 * they can have the game. Deliberately **not** `.capsulePrice`: that treatment
 * is tabular numerals at negative tracking, built for a figure, and a phrase set
 * in it reads as a broken price. This is the data-label treatment in the accent
 * instead — a state, in the colour the price goes on hover.
 */
.capsuleStatus {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--gb-accent);
}

/* --- Badges ------------------------------------------------------------- */

.badge {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.15rem 0.4rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

/*
 * Live now, and counting down.
 *
 * Both were their own channel — Volt for live, Solar for a countdown — which is
 * two colours a panel does not own, on top of its accent. They are separated by
 * treatment instead: live is a solid field, a countdown is an outline. Solid
 * beats outline at a glance, which is the right way round, because live is the
 * rarer and more urgent of the two.
 *
 * The dot in the live badge is `currentColor`, so it follows either.
 */
.badgeLive {
  background: var(--gb-accent);
  color: var(--gb-on-accent);
  font-weight: 700;
}

.badgeCountdown {
  border: 1px solid var(--gb-accent);
  color: var(--gb-accent);
  font-weight: 700;
}

.badgeQuiet {
  border: 1px solid var(--gb-rule);
  color: var(--gb-text-muted);
}

.dot {
  width: 0.4rem;
  height: 0.4rem;
  background: currentColor;
}

/* --- Grids and rails ---------------------------------------------------- */

.grid {
  display: grid;
  /* Wider tracks than a text card grid: a capsule is art first, and art needs
     room before it stops being a thumbnail. */
  grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
  gap: var(--gb-space-6);
}

.gridPortrait {
  grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
}

/*
 * A rail. Horizontal on wide screens, wrapping grid on narrow ones.
 *
 * Scroll snap so a flick lands on a capsule edge rather than halfway through
 * somebody's key art.
 *
 * **The padding is what makes the halo visible here.** A scroll container clips
 * at its padding edge, so without it the accent frame standing 12px proud of a
 * capsule is shaved off against the end of the scrollport — on the first and
 * last cards in every row, which are the two a visitor is most likely to point
 * at. The grid next door has no overflow of its own and needs none of this.
 *
 * The inline padding is taken straight back out with a negative margin, so the
 * first capsule still lines up with the heading above it and the row bleeds
 * into the page's gutter, which is wider than this. The block padding is not
 * taken back: it is the room the lift needs, and the bottom of it was already
 * here for the scrollbar.
 */
.rail {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(17rem, 1fr);
  gap: var(--gb-space-6);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding: var(--gb-space-3);
  margin-inline: calc(var(--gb-space-3) * -1);
  /* So a snap lands the card inside the padding rather than flush against the
     edge of the scrollport, which would clip the very frame the padding is for. */
  scroll-padding-inline: var(--gb-space-3);
  scrollbar-width: thin;
}

.rail > * {
  scroll-snap-align: start;
}

.railHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--gb-space-4);
  flex-wrap: wrap;
  border-bottom: 2px solid var(--gb-text);
  padding-bottom: var(--gb-space-2);
  margin-bottom: var(--gb-space-4);
}

.railLink {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gb-text-muted);
  text-decoration: none;
  white-space: nowrap;
}

.railLink:hover {
  color: var(--gb-text);
}

/* --- Hero --------------------------------------------------------------- */

/*
 * The store hero. Full-bleed, art on Ink, type over it.
 *
 * Epic and Steam both open on one title at scale. This is that shape, and it
 * only renders when there is a title to put in it — an empty hero with stock
 * art would be the exact fabrication section 29.3 forbids.
 */
.hero {
  position: relative;
  background: var(--gb-ink);
  color: var(--gb-paper);
  min-height: clamp(20rem, 44vh, 32rem);
  display: flex;
  align-items: flex-end;
  overflow: hidden;
}

.heroArt {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Dimmed with a flat overlay rather than a gradient scrim. */
  opacity: 0.55;
}

.heroTall {
  min-height: clamp(28rem, 76vh, 48rem);
}

.heroInner {
  position: relative;
  z-index: 1;
  padding: var(--gb-space-12) var(--gb-space-8);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
  width: 100%;
  /* Without this the block is 4rem wider than the hero, and `.hero` clips
     (`overflow: hidden`) — so the right-hand padding, and any title long enough
     to reach it, were being cut off rather than laid out. */
  box-sizing: border-box;
}

.heroMeta {
  display: flex;
  gap: var(--gb-space-4);
  align-items: center;
  flex-wrap: wrap;
}

/* --- Buy rail ----------------------------------------------------------- */

/*
 * The price column on a store page.
 *
 * Sticky on wide screens so the price and the buy action stay reachable while
 * a reader scrolls the measured blocks. Those blocks are long by design, and a
 * buy button that scrolls away at the top of a long page is the most expensive
 * layout mistake a store can make.
 */
.buyRail {
  position: sticky;
  top: var(--gb-space-6);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
}

.buyCard {
  border: 1px solid var(--gb-text);
  padding: var(--gb-space-6);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
  background: var(--gb-surface-raised);
}

.buyPrice {
  font-family: var(--gb-font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-weight: 700;
  font-size: clamp(2rem, 4vw, 2.75rem);
  line-height: 1;
  letter-spacing: -0.04em;
}

.factList {
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--gb-space-2) var(--gb-space-4);
  border-top: 1px solid var(--gb-rule);
  padding-top: var(--gb-space-4);
}

.factList dt {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gb-text-faint);
}

.factList dd {
  margin: 0;
  font-size: var(--gb-body);
}

/*
 * The two actions in the buy rail.
 *
 * **They lived in a scoped `<style>` block on the Astro store page**, written
 * out with `:global` so the rules would survive Astro's scoping — which is what
 * a page has to do when it is the only thing that draws a control. The store
 * page is a component in the React app now and sits beside the rail it is part
 * of, so the styles come here, next to `.buyCard` and `.buyPrice`, where the
 * rest of that rail already is.
 *
 * Not `ui.button`, and the difference is the shape rather than the colour: this
 * one is full width and mono, because it sits in a narrow column under a price
 * set in the same face and a rounded inline button reads as belonging to a form
 * rather than to a shop.
 */
.buyAction {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-ui-label);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: var(--gb-space-3) var(--gb-space-6);
  background: var(--gb-accent);
  color: var(--gb-on-accent);
  border: var(--gb-rule-width) solid var(--gb-accent);
  cursor: pointer;
  width: 100%;
  display: block;
  text-align: center;
  text-decoration: none;
  box-sizing: border-box;
}

/*
 * Who the buyer is buying from, under the button.
 *
 * Small and quiet, and it still has to be legible — this is the merchant of
 * record disclosure, it used to have a screen of its own before buying went
 * straight to the checkout, and it is the one sentence in the rail somebody is
 * owed rather than offered. `--gb-ui-label` is the smallest size in the system
 * that is still set for reading.
 */
.buyNote {
  font-size: var(--gb-ui-label);
  line-height: 1.5;
  color: var(--gb-text-muted);
  margin: 0;
}

/*
 * Where else the game is sold, inside the buy card.
 *
 * Separated by a rule rather than by space alone. The card already holds a
 * price, a button and a fact list; a fourth block with only a gap above it reads
 * as part of the facts, and these are links rather than facts.
 */
.alsoOn {
  border-top: var(--gb-rule-width) solid var(--gb-rule);
  padding-top: var(--gb-space-4);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-3);
}

.alsoOnTitle {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gb-text-muted);
  margin: 0;
}

/*
 * Stacked and full width, matching `buyAction` above them.
 *
 * They were a wrapping row of chips when this block sat under the description in
 * a six-column layout. In a rail that narrow, chips wrap after one or two and
 * leave a ragged edge beside a button that is full width — so they take the same
 * shape as the button and read as a short list of destinations.
 *
 * Outlined rather than filled, because the card has exactly one call to action
 * and sending somebody to Steam is not it.
 */
.storeLinks {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
  list-style: none;
  margin: 0;
  padding: 0;
}

.storeLink {
  display: block;
  box-sizing: border-box;
  width: 100%;
  text-align: center;
  font-family: var(--gb-font-mono);
  font-size: var(--gb-ui-label);
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: var(--gb-space-2) var(--gb-space-3);
  border: var(--gb-rule-width) solid var(--gb-accent);
  color: var(--gb-accent);
  text-decoration: none;
}

.storeLink:hover,
.storeLink:focus-visible {
  background: var(--gb-accent);
  color: var(--gb-on-accent);
}

/*
 * The imported description, which is the one place this application renders
 * markup it did not author.
 *
 * Every rule here is defensive as well as typographic. `img` is capped at the
 * column width and `height: auto`, because a Steam page's screenshots are 600px
 * wide or wider and nothing about the incoming markup promises a size. Tables
 * and long unbroken strings scroll inside the block rather than widening the
 * page — `overflow-wrap` is what stops a URL in a description pushing the buy
 * rail off the side on a phone.
 *
 * The element selectors are scoped to this class on purpose: they apply to
 * sanitised third-party markup and to nothing else on the site.
 */
.description {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
  overflow-wrap: anywhere;
}

.description > * {
  margin: 0;
}

.description p {
  font-family: var(--gb-font-text);
  font-size: var(--gb-body);
  line-height: 1.6;
}

.description h2,
.description h3 {
  font-family: var(--gb-font-display);
  line-height: 1.2;
  margin-top: var(--gb-space-2);
}

.description h2 {
  font-size: var(--gb-subhead);
}

.description h3 {
  font-size: var(--gb-body);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.description ul,
.description ol {
  padding-left: var(--gb-space-6);
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-2);
  font-family: var(--gb-font-text);
  font-size: var(--gb-body);
  line-height: 1.6;
}

.description blockquote {
  border-left: var(--gb-rule-width) solid var(--gb-accent);
  padding-left: var(--gb-space-4);
  font-family: var(--gb-font-text);
  font-size: var(--gb-body);
  line-height: 1.6;
}

.description img {
  display: block;
  max-width: 100%;
  height: auto;
}

/*
 * Steam's markup uses `<br>` for spacing between blocks, and the sanitiser keeps
 * at most two in a row. Two blank lines inside a paragraph is a paragraph gap
 * drawn the hard way, so it is given roughly the height of one.
 */
.description br {
  line-height: 1.9;
}

/* --- Arrival ------------------------------------------------------------ */

/*
 * The shelf is dealt out rather than switched on.
 *
 * A grid of capsules appearing together is a page finishing loading. The same
 * grid arriving one card after another is a hand being dealt, which is the
 * shape a storefront wants and the thing the store had no version of: every
 * piece of motion in this file was a response to a pointer, so a shelf nobody
 * had touched yet was completely still.
 *
 * **Opacity only, and that is a constraint rather than a preference.** The card
 * is a three-dimensional object — `.capsule` supplies a perspective and
 * `.capsuleFace` turns inside it — and a `transform` on the card itself would
 * put a second, competing transform in front of that lens. A `clip-path` would
 * be worse: the halo stands twelve pixels outside the card by design, and a
 * clip settling on the border box would shave it off for the rest of the visit.
 * A fade compounds with the page's own arrival above and needs neither.
 *
 * The stagger is `--gb-stagger`, the same step the page and every `RevealGroup`
 * use, and it stops at the eighth card. Nobody watches the nineteenth capsule
 * wait its turn, and a full shelf would otherwise take three seconds to land.
 *
 * `backwards` and never `forwards`, here as everywhere else in this system. The
 * backwards half is what holds a card invisible through its stagger delay; a
 * forwards fill would leave every one of these pinned at `opacity: 1` by the
 * animation for the rest of the visit, which beats any normal declaration in
 * the cascade and would quietly override anything that later wanted to fade a
 * capsule for a reason of its own.
 */
.grid > *,
.rail > * {
  animation: gb-shelf-deal var(--gb-motion-slow) var(--gb-ease-out) backwards;
}

.grid > *:nth-child(2),
.rail > *:nth-child(2) {
  animation-delay: var(--gb-stagger);
}
.grid > *:nth-child(3),
.rail > *:nth-child(3) {
  animation-delay: calc(var(--gb-stagger) * 2);
}
.grid > *:nth-child(4),
.rail > *:nth-child(4) {
  animation-delay: calc(var(--gb-stagger) * 3);
}
.grid > *:nth-child(5),
.rail > *:nth-child(5) {
  animation-delay: calc(var(--gb-stagger) * 4);
}
.grid > *:nth-child(6),
.rail > *:nth-child(6) {
  animation-delay: calc(var(--gb-stagger) * 5);
}
.grid > *:nth-child(7),
.rail > *:nth-child(7) {
  animation-delay: calc(var(--gb-stagger) * 6);
}
.grid > *:nth-child(n + 8),
.rail > *:nth-child(n + 8) {
  animation-delay: calc(var(--gb-stagger) * 7);
}

@keyframes gb-shelf-deal {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/*
 * The hero settles.
 *
 * The art comes in a few per cent large and comes to rest at its own size, once,
 * over about a second and a half. That is the one place on the site where the
 * motion is allowed to be slow enough to notice, and it is the right place: the
 * hero is a single piece of key art at the top of the page, which is the shape
 * every trailer in this industry opens on.
 *
 * **One-shot, and it ends at exactly 1.** A slow drift that never stops is the
 * usual version of this effect and it is two things this brand is not: it never
 * resolves, and it is a background animating for as long as somebody is reading
 * over it, which on a laptop is a fan spinning up. Ending on the image's own
 * size also means it is pixel-accurate for the whole time anybody is actually
 * looking at it.
 *
 * The overshoot is clipped by `.hero`, which already has `overflow: hidden` for
 * the type inside it, so nothing extends the layout.
 */
.heroArt {
  animation:
    gb-hero-settle calc(var(--gb-motion-slow) * 4) var(--gb-ease-out) backwards,
    gb-hero-push var(--gb-camera-push) var(--gb-ease-out) calc(var(--gb-motion-slow) * 4) forwards;
  transform-origin: 50% 45%;
}

@keyframes gb-hero-push {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.07);
  }
}

@keyframes gb-hero-settle {
  from {
    transform: scale(1.05);
  }
  to {
    transform: scale(1);
  }
}

/*
 * What is written over it arrives after it, line by line.
 *
 * The studio credit, then the title, then the price and the button — in reading
 * order, on the same step as everything else. The title is the one piece of
 * display type on the store's front page and this is what gives it an entrance
 * rather than a paint.
 */
.heroInner > * {
  animation: gb-hero-deal var(--gb-motion-slow) var(--gb-ease-out) backwards;
}

.heroInner > *:nth-child(2) {
  animation-delay: var(--gb-stagger);
}
.heroInner > *:nth-child(3) {
  animation-delay: calc(var(--gb-stagger) * 2);
}
.heroInner > *:nth-child(n + 4) {
  animation-delay: calc(var(--gb-stagger) * 3);
}

@keyframes gb-hero-deal {
  from {
    opacity: 0;
    transform: translateY(0.875rem);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/*
 * Reduced motion: everything arrives finished.
 *
 * Cancelled outright rather than left to the global reset in `tokens.css`,
 * which collapses an animation to a single 0.01ms iteration — that lands each
 * of these on its end state, which is correct here and only by luck. Naming
 * `animation: none` is what makes the resting state a decision.
 */
@media (prefers-reduced-motion: reduce) {
  .grid > *,
  .rail > *,
  .heroArt,
  .heroInner > * {
    animation: none;
    transform: none;
  }
}

.gallery {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-3);
}

.galleryStage {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: var(--gb-rule-width) solid var(--gb-rule);
  background: var(--gb-surface-sunken);
  overflow: hidden;
}

.galleryStage img,
.galleryStage video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000000;
}

.galleryStrip {
  display: flex;
  gap: var(--gb-space-2);
  overflow-x: auto;
  padding-bottom: var(--gb-space-2);
  scrollbar-width: thin;
}

.galleryThumb {
  position: relative;
  flex: 0 0 auto;
  width: 7.5rem;
  aspect-ratio: 16 / 9;
  padding: 0;
  border: var(--gb-rule-width) solid var(--gb-rule);
  background: var(--gb-surface-sunken);
  cursor: pointer;
  overflow: hidden;
}

.galleryThumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.72;
  transition: opacity var(--gb-motion-quick, 140ms) ease;
}

.galleryThumb:hover img,
.galleryThumb:focus-visible img {
  opacity: 1;
}

.galleryThumbCurrent {
  border-color: var(--gb-accent);
}

.galleryThumbCurrent img {
  opacity: 1;
}

.galleryThumbPlate {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gb-text-faint);
}

.galleryPlayBadge {
  position: absolute;
  inset: auto var(--gb-space-1) var(--gb-space-1) auto;
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0 var(--gb-space-1);
  background: var(--gb-accent);
  color: var(--gb-on-accent);
  pointer-events: none;
}

.galleryCount {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gb-text-faint);
  margin: 0;
}
